import styled from "@emotion/styled"
import { PButton, PHeading, PIcon, PTag } from "@porsche-design-system/components-react"
import {
	spacingFluidSm,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { HeaderView, LocalizedLink } from "@slfinrtl/ui-bff-routes"
import type { JSX, PropsWithChildren } from "react"
import { FormattedMessage } from "react-intl"
import { Link } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import {
	ContentWrapper,
	CopyToClipboardInsideTag,
	Flex,
	HoverableFlyout,
	HoverableFlyoutMenu,
	StatusBadge,
} from "src/components"
import { useConfig } from "src/hooks"
import { customerFeedbackMessages } from "../../overviewContainer/customerFeedbackContainer/components/customerFeedback.messages"
import { OpenListingButtonLink } from "./OpenListingButtonLink"
import { PmpStatusMessage } from "./PmpStatusMessage"
import { StatusMessage } from "./StatusMessage"
import { headerMessages } from "./header.messages"
import { HeaderSticky } from "./sticky/HeaderSticky"

export interface HeaderProps {
	actionMenu: JSX.Element[][]
	customerPrice: string | undefined | null
	inventoryId: string
	inventoryNumber: string | undefined | null
	onOpenListingClick: (links: LocalizedLink[]) => Promise<void>
	onShowCustomerFeedbackClick: () => void
	view: HeaderView
}

export const Header = (props: HeaderProps) => {
	const {
		actionMenu,
		customerPrice,
		inventoryId,
		inventoryNumber,
		onOpenListingClick,
		onShowCustomerFeedbackClick,
		view,
	} = props
	const { badge, customerFeedback, location, openListing, saleId, statusMessage, title, vin } = view
	const routes = useDealerAppRoutes()
	const saleDetailsUrl = saleId !== undefined ? routes?.salesList?.saleDetails?.url(saleId) : undefined

	return (
		<StyledContentWrapper as="header">
			<Flex $justifyContent="space-between" $gap="sm">
				<HeadingWrapper>
					<PHeading size="2xl" tag="h1">
						{title}
					</PHeading>
					<Flex $wrap="wrap" $gap="xs">
						<StatusBadge status={badge.status} autoWidth data-testid="sales-status-badge" />
						{vin && (
							<PTag variant="secondary">
								<CopyToClipboardInsideTag value={vin} />
							</PTag>
						)}
						{inventoryNumber && inventoryNumber !== emptyPlaceholder && (
							<PTag variant="secondary">{inventoryNumber}</PTag>
						)}
						{customerPrice && <PTag variant="secondary">{customerPrice}</PTag>}

						{statusMessage && (
							<PTag variant="secondary">
								<StatusMessage inventoryId={inventoryId} view={statusMessage} />
							</PTag>
						)}
						{statusMessage?.secondaryLink && statusMessage.secondaryMessage && (
							<PTag variant="secondary">
								<PmpStatusMessage view={statusMessage} />
							</PTag>
						)}
						{saleDetailsUrl && (
							<PTag variant="secondary">
								<PIcon name="arrow-right" color="inherit" size="2xs" />
								<Link to={saleDetailsUrl} data-testid="header_sale-details-link">
									<FormattedMessage {...headerMessages.saleDetailsPage} />
								</Link>
							</PTag>
						)}
						{location && <PTag variant="secondary">{location}</PTag>}
						{customerFeedback && (
							<PTag variant="secondary">
								<button type="button" onClick={onShowCustomerFeedbackClick}>
									{customerFeedback.feedback.length > 0 ? (
										<FormattedMessage
											{...customerFeedbackMessages.feedback}
											values={{ count: customerFeedback.feedback.length }}
										/>
									) : (
										<FormattedMessage {...customerFeedbackMessages.headerNoFeedback} />
									)}
								</button>
							</PTag>
						)}
					</Flex>
				</HeadingWrapper>
				<ButtonsWrapper>
					<OpenListingButtonLink onOpenListingClick={onOpenListingClick} openListing={openListing} />
					<ActionMenuElement actionMenu={actionMenu} />
				</ButtonsWrapper>
			</Flex>
			<HeaderSticky
				actionMenu={<ActionMenuElement actionMenu={actionMenu} />}
				view={view}
				onOpenListingClick={onOpenListingClick}
				inventoryNumber={inventoryNumber}
				customerPrice={customerPrice}
			/>
		</StyledContentWrapper>
	)
}

interface ActionMenuElementProps {
	actionMenu: JSX.Element[][]
}

const ActionMenuElement = ({ actionMenu }: ActionMenuElementProps) => {
	const { monroneyLabelEnabled } = useConfig()
	return (
		<HoverableFlyout
			trigger={
				<PButton
					type="button"
					variant="secondary"
					data-testid="action-menu"
					icon="menu-dots-vertical"
					hideLabel={{ base: true, m: false }}
				>
					<FormattedMessage {...headerMessages.actionButton} />
				</PButton>
			}
			align="right"
			width={monroneyLabelEnabled ? "default" : "narrow"}
		>
			<HoverableFlyoutMenu menu={actionMenu} />
		</HoverableFlyout>
	)
}

const StyledContentWrapper = styled(ContentWrapper)`
	padding-top: ${spacingStaticMd};
	padding-bottom: ${spacingStaticLg};
`

const HeadingWrapper = styled(({ children, ...rest }: PropsWithChildren) => (
	<Flex $direction="column" {...rest}>
		{children}
	</Flex>
))`
	gap: ${spacingStaticSm};
`

const ButtonsWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(2, auto);
	gap: ${spacingFluidSm};
	align-self: start;
`
